import { Tooltip } from "antd";
import { GetColumnCheckboxGroupFilterDropdown } from "../../commonComponents/listTable/CustomFilter";
import ListColumnBase from "../../commonComponents/listBase/listColumnBase";
import "../../../base/basicComponents/status/statusDiv.scss";
import { ColumnsType } from "antd/lib/table";
import type { IRequestListFilter, MyRequestListResponse } from "service/myRequestListService/types";
import { ColumnsVisibleType } from "./columnSelectOption";
import { MyRequestTableFilterNameLower } from "./types";

const { mapFilter, handleFilteredValue, handleWidth, handleTableSortOrder, formatDate } =
  ListColumnBase();

export default function MyRequestListColumn({
  myRequestListFilterData,
  filteredInfo,
  columnsVisible,
  tableSortData,
  handleColumnFilterClick,
  filterLoading,
}: {
  myRequestListFilterData: IRequestListFilter;
  columnsVisible: ColumnsVisibleType;
  filterLoading: boolean;
  filteredInfo: Partial<Record<MyRequestTableFilterNameLower, string[] | number[]>>;
  tableSortData: {
    sortColumn: string;
    isAscending: boolean;
  }[];
  handleColumnFilterClick: (flag: boolean) => void;
}) {
  const columns: ColumnsType<MyRequestListResponse["data"][number]> = [
    {
      title: "PMP Transaction ID",
      dataIndex: "transactionId",
      width: handleWidth(!columnsVisible.transactionIdShow, 160),
      ellipsis: {
        showTitle: false,
      },
      render: (transactionId) => (
        <Tooltip placement="topLeft" title={transactionId}>
          {transactionId}
        </Tooltip>
      ),
    },
    {
      title: "Account Code",
      dataIndex: "accountAbbr",
      //@ts-ignore dbNameis for sorting use
      dbName: "AccountAbbr",
      sorter: true,
      sortOrder: handleTableSortOrder("AccountAbbr", tableSortData),
      width: handleWidth(!columnsVisible.accountAbbrShow, 145),
      ellipsis: {
        showTitle: false,
      },
      render: (accountAbbreviation) => (
        <Tooltip placement="topLeft" title={accountAbbreviation}>
          {accountAbbreviation}
        </Tooltip>
      ),
    },
    {
      title: "Account Name",
      dataIndex: "accountName",
      ellipsis: {
        showTitle: false,
      },
      render: (accountName) => (
        <Tooltip placement="topLeft" title={accountName}>
          {accountName}
        </Tooltip>
      ),
      width: handleWidth(!columnsVisible.accountNameShow, 220),
    },
    {
      title: "Task Group",
      dataIndex: "taskGroupName",
      //@ts-ignore
      dbName: "TaskGroupName",
      filters: mapFilter(myRequestListFilterData.taskGroupNames),
      filteredValue: handleFilteredValue(filteredInfo.taskGroupName),
      onFilterDropdownOpenChange: (e) => handleColumnFilterClick(e),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, filters }) =>
        GetColumnCheckboxGroupFilterDropdown({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
          filters,
          filterLoading,
        }),
      sorter: true,
      sortOrder: handleTableSortOrder("TaskGroupName", tableSortData),
      width: handleWidth(!columnsVisible.taskGroupShow, 220),
      ellipsis: {
        showTitle: false,
      },
      render: (taskGroup) => (
        <Tooltip placement="topLeft" title={taskGroup}>
          {taskGroup}
        </Tooltip>
      ),
    },
    {
      title: "Category",
      dataIndex: "categoryName",
      //@ts-ignore
      dbName: "CategoryName",
      filters: mapFilter(myRequestListFilterData.categoryNames), // Uses default filtering behavior
      filteredValue: handleFilteredValue(filteredInfo.categoryName),
      onFilterDropdownOpenChange: (e) => handleColumnFilterClick(e),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, filters }) =>
        GetColumnCheckboxGroupFilterDropdown({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
          filters,
          filterLoading,
        }),
      sorter: true,
      sortOrder: handleTableSortOrder("CategoryName", tableSortData),
      width: handleWidth(!columnsVisible.categoryShow, 220),
      ellipsis: {
        showTitle: false,
      },
      render: (category) => (
        <Tooltip placement="topLeft" title={category}>
          {category}
        </Tooltip>
      ),
    },
    {
      title: "Task ID",
      dataIndex: "taskId",
      width: handleWidth(!columnsVisible.taskIdShow, 100),
      ellipsis: {
        showTitle: false,
      },
      render: (taskId) => (
        <Tooltip placement="topLeft" title={taskId}>
          {taskId}
        </Tooltip>
      ),
    },
    {
      title: "Task Label Name",
      dataIndex: "taskLabelName",
      //@ts-ignore
      dbName: "TaskLabelName",
      filters: mapFilter(myRequestListFilterData.taskLabelNames),
      filteredValue: handleFilteredValue(filteredInfo.taskLabelName),
      onFilterDropdownOpenChange: (e) => handleColumnFilterClick(e),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, filters }) =>
        GetColumnCheckboxGroupFilterDropdown({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
          filters,
          filterLoading,
        }),
      sorter: true,
      sortOrder: handleTableSortOrder("TaskLabelName", tableSortData),
      width: handleWidth(!columnsVisible.taskLabelNameShow, 220),
      ellipsis: {
        showTitle: false,
      },
      render: (taskLabelName) => (
        <Tooltip placement="topLeft" title={taskLabelName}>
          {taskLabelName}
        </Tooltip>
      ),
    },
    {
      title: "Highly Regulated?",
      dataIndex: "highlyRegulated",
      filters: mapFilter(myRequestListFilterData.highlyRegulated),
      filteredValue: handleFilteredValue(filteredInfo.highlyRegulated),
      onFilterDropdownOpenChange: (e) => handleColumnFilterClick(e),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, filters }) =>
        GetColumnCheckboxGroupFilterDropdown({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
          filters,
          filterLoading,
        }),
      width: handleWidth(!columnsVisible.highlyRegulatedShow, 170),
      ellipsis: {
        showTitle: false,
      },
      render: (highlyRegulated) => (
        <Tooltip placement="topLeft" title={highlyRegulated}>
          {highlyRegulated}
        </Tooltip>
      ),
    },
    {
      title: "Primary Procedure Owner",
      dataIndex: "primaryProcedureOwnerName",
      filters: mapFilter(myRequestListFilterData.primaryProcedureOwnerNames),
      filteredValue: handleFilteredValue(filteredInfo.primaryProcedureOwnerName),
      onFilterDropdownOpenChange: (e) => handleColumnFilterClick(e),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, filters }) =>
        GetColumnCheckboxGroupFilterDropdown({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
          filters,
          filterLoading,
        }),
      width: handleWidth(!columnsVisible.primaryProcedureOwnerShow, 245),
      ellipsis: {
        showTitle: false,
      },
      render: (primaryProcedureOwner) => (
        <Tooltip placement="topLeft" title={primaryProcedureOwner}>
          {primaryProcedureOwner}
        </Tooltip>
      ),
    },
    {
      title: "Procedure Owner",
      dataIndex: "procedureOwnerName",
      filters: mapFilter(myRequestListFilterData.procedureOwnerNames),
      filteredValue: handleFilteredValue(filteredInfo.procedureOwnerName),
      onFilterDropdownOpenChange: (e) => handleColumnFilterClick(e),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, filters }) =>
        GetColumnCheckboxGroupFilterDropdown({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
          filters,
          filterLoading,
        }),
      width: handleWidth(!columnsVisible.procedureOwnerShow, 190),
      ellipsis: {
        showTitle: false,
      },
      render: (procedureOwner) => (
        <Tooltip placement="topLeft" title={procedureOwner}>
          {procedureOwner}
        </Tooltip>
      ),
    },
    {
      title: "Process Category",
      dataIndex: "processCategoryName",
      //@ts-ignore
      dbName: "ProcessCategoryName",
      sorter: true,
      sortOrder: handleTableSortOrder("ProcessCategoryName", tableSortData),
      width: handleWidth(!columnsVisible.processCategoryShow, 220),
      ellipsis: {
        showTitle: false,
      },
      render: (text, record) => (
        <Tooltip placement="topLeft" title={text}>
          {text === "Archive Procedure" || text === "Archive Task Info." ? (
            text
          ) : (
            <a
              href={"/ProcessDetail/" + record.processID}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`my-request-process-link-${record.processID}`}
            >
              {text}
            </a>
          )}
        </Tooltip>
      ),
    },
    {
      title: "Trigger Process",
      dataIndex: "triggerProcessName",
      //@ts-ignore
      dbName: "TriggerProcessName",
      filters: mapFilter(myRequestListFilterData.triggerProcessNames),
      filteredValue: handleFilteredValue(filteredInfo.triggerProcessName),
      onFilterDropdownOpenChange: (e) => handleColumnFilterClick(e),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, filters }) =>
        GetColumnCheckboxGroupFilterDropdown({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
          filters,
          filterLoading,
        }),
      sorter: true,
      sortOrder: handleTableSortOrder("TriggerProcessName", tableSortData),
      width: handleWidth(!columnsVisible.triggerProcessShow, 220),
      ellipsis: {
        showTitle: false,
      },
      render: (triggerProcess, record) => (
        <Tooltip placement="topLeft" title={triggerProcess}>
          <a
            href={"/ProcessDetail/" + record.parentProcessID}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`my-request-parent-process-link-${record.parentProcessID}`}
          >
            {triggerProcess}
          </a>
        </Tooltip>
      ),
    },
    {
      title: "Process Status",
      dataIndex: "processStatus",
      //@ts-ignore
      dbName: "ProcessStatus",
      sorter: true,
      sortOrder: handleTableSortOrder("ProcessStatus", tableSortData),
      width: handleWidth(!columnsVisible.processStatusShow, 220),
      ellipsis: {
        showTitle: false,
      },
      render: (processStatus) => {
        let className;

        switch (processStatus) {
          case "Pending Approval":
            className = "pendingApproval";
            break;
          case "Pending Submission":
            className = "pendingSubmission";
            break;
          case "Rejected":
            className = "rejected";
            break;
          case "Approved":
            className = "approval";
            break;
          case "System Termination":
            className = "systemTermination";
            break;
          default:
            className = "";
        }
        return (
          <Tooltip placement="topLeft" title={processStatus}>
            <div className={`MyRequestProcessStatusDiv ${className}`}>{processStatus}</div>
          </Tooltip>
        );
      },
    },
    {
      title: "Current Processor",
      dataIndex: "currentProcessor",
      //@ts-ignore
      dbName: "CurrentProcessor",
      filters: mapFilter(myRequestListFilterData.currentProcessor),
      filteredValue: handleFilteredValue(filteredInfo.currentProcessor),
      onFilterDropdownOpenChange: (e) => handleColumnFilterClick(e),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, filters }) =>
        GetColumnCheckboxGroupFilterDropdown({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
          filters,
          filterLoading,
        }),
      sorter: true,
      sortOrder: handleTableSortOrder("CurrentProcessor", tableSortData),
      width: handleWidth(!columnsVisible.currentProcessorShow, 243),
      ellipsis: {
        showTitle: false,
      },
      render: (currentProcessor) => (
        <Tooltip placement="topLeft" title={currentProcessor}>
          {currentProcessor}
        </Tooltip>
      ),
    },
    {
      title: "Process Initiate Date",
      dataIndex: "processInitiateDate",
      //@ts-ignore
      dbName: "ProcessInitiateDate",
      sorter: true,
      sortOrder: handleTableSortOrder("ProcessInitiateDate", tableSortData),
      width: handleWidth(!columnsVisible.processInitiateDateShow, 185),
      ellipsis: {
        showTitle: false,
      },
      render: (processInitiateDate) => (
        <Tooltip placement="topLeft" title={formatDate(processInitiateDate)}>
          {formatDate(processInitiateDate)}
        </Tooltip>
      ),
    },
    {
      title: "Process Initiator",
      dataIndex: "processInitiatorName",
      filters: mapFilter(myRequestListFilterData.processInitiatorNames),
      filteredValue: handleFilteredValue(filteredInfo.processInitiatorName),
      onFilterDropdownOpenChange: (e) => handleColumnFilterClick(e),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, filters }) =>
        GetColumnCheckboxGroupFilterDropdown({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
          filters,
          filterLoading,
        }),
      width: handleWidth(!columnsVisible.processInitiatorShow, 155),
      ellipsis: {
        showTitle: false,
      },
      render: (processInitiator) => (
        <Tooltip placement="topLeft" title={processInitiator}>
          {processInitiator}
        </Tooltip>
      ),
    },
    {
      title: "Process Completed Date",
      dataIndex: "processCompletedDate",
      //@ts-ignore
      dbName: "ProcessCompletedDate",
      sorter: true,
      sortOrder: handleTableSortOrder("ProcessCompletedDate", tableSortData),
      width: handleWidth(!columnsVisible.processCompletedDateShow, 210),
      ellipsis: {
        showTitle: false,
      },
      render: (processCompletedDate) => {
        if (processCompletedDate) {
          return (
            <Tooltip placement="topLeft" title={formatDate(processCompletedDate)}>
              {formatDate(processCompletedDate)}
            </Tooltip>
          );
        }
        return <></>;
      },
    },
    {
      title: "",
      dataIndex: "space",
      // render: () => {},
      width: 1,
    },
  ];

  return columns;
}
